import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, CheckCheck, LogOut } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  EVACUATION_EVENT_PARTICIPANTS_STATUSES,
  EVACUATION_METHODS,
  PAGE_SIZE,
  SYSTEM_DETAIL_CODES,
  TABLE_IDS,
} from "../../../constants/common";
import { cn } from "../../../utils/cn";
import ViewAttachmentModal from "../../modal/ViewAttachmentModal";
import QRScannerComponent from "../../qrScanner/QRScannerComponent";
import { useGetAllEvacuationEventsParticipants } from "../../../hooks/emergency-evacuation/query";
import { EvacuationEventParticipant } from "../../../hooks/emergency-evacuation/interface";
import SafeEvacuationEventParticipantModal from "../../modal/SafeEvacuationEventParticipantModal";
import ExitedEvacuationEventParticipantModal from "../../modal/ExitedEvacuationEventParticipantModal";
import { Controller, useForm } from "react-hook-form";
import ComboboxInput from "../../inputs/ComboboxInput";
import {
  formattedDateTimeWithoutSec,
  mapToOptions,
} from "../../../lib/functions";
import { useGetAllSystemDetailCodesDropdown } from "../../../hooks/common/query";
import Loader from "../../loader";

interface MusterStationTableProps {
  setSelectedStatus: Dispatch<SetStateAction<number>>;
  selectedMusterPoint: number;
}

const MusterStationTable = ({
  setSelectedStatus,
  selectedMusterPoint,
}: MusterStationTableProps) => {
  const { i18n, t } = useTranslation();

  const { control, watch } = useForm({
    defaultValues: {
      status: EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING.toString(),
    },
  });

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [evacuationEventsParticipants, setEvacuationEventsParticipants] =
    useState([]);

  const [imageSrc, setImageSrc] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [
    isSafeEvacuationEventParticipantModalOpen,
    setIsSafeEvacuationEventParticipantModalOpen,
  ] = useState(false);
  const [
    isExitedEvacuationEventParticipantModalOpen,
    setIsExitedEvacuationEventParticipantModalOpen,
  ] = useState(false);

  const status = watch("status");

  const {
    data: allEvacuationEventsParticipants,
    isPending: isAllEvacuationEventsParticipantsLoading,
    refetch: refetchAllEvacuationEventsParticipants,
  } = useGetAllEvacuationEventsParticipants({
    s_EvacuationStatus: status,
    s_MusterPointIDs: selectedMusterPoint
      ? selectedMusterPoint?.toString()
      : "",
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  const { data: allEventStatuses } = useGetAllSystemDetailCodesDropdown({
    encryptedId:
      SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_EVENT_PARITICIPANT_STATUSES,
  });

  useEffect(() => {
    if (allEvacuationEventsParticipants) {
      setEvacuationEventsParticipants(
        allEvacuationEventsParticipants.data || [],
      );
      setPages(allEvacuationEventsParticipants.pagination?.pages || 1);
      setCount(allEvacuationEventsParticipants.pagination?.count || 0);
    }
  }, [
    setCount,
    setEvacuationEventsParticipants,
    setPages,
    allEvacuationEventsParticipants,
  ]);

  useEffect(() => {
    setPage(1);
  }, [search, setPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchAllEvacuationEventsParticipants();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetchAllEvacuationEventsParticipants]);

  const evacuationStatuses = useMemo(() => {
    return mapToOptions(
      "s_Desc_E",
      "s_Desc_A",
      "n_DetailedCode",
      allEventStatuses,
    );
  }, [allEventStatuses]);

  const onPhotoClick = useCallback((attachment: string) => {
    setIsViewAttachmentModalOpen(true);
    setImageSrc(attachment);
  }, []);

  const handleSafe = useCallback(
    (eventId: number) => {
      if (!selectedMusterPoint) {
        toast.error(t("pleaseSelectMusterPoint"));
        return;
      }

      setIsSafeEvacuationEventParticipantModalOpen(true);
      setSelectedEventId(eventId);
    },
    [selectedMusterPoint, t],
  );

  const handleExited = useCallback((eventId: number) => {
    setSelectedEventId(eventId);
    setIsExitedEvacuationEventParticipantModalOpen(true);
  }, []);

  const columns: ColumnDef<EvacuationEventParticipant>[] = useMemo(
    () => [
      {
        accessorKey: "s_EvacuationStatus_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1 max-w-[100px]",
                row.original.n_EvacuationStatus ===
                  EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING
                  ? "bg-red-500"
                  : row.original.n_EvacuationStatus ===
                      EVACUATION_EVENT_PARTICIPANTS_STATUSES.CANCELLED
                    ? "bg-orange-600"
                    : row.original.n_EvacuationStatus ===
                        EVACUATION_EVENT_PARTICIPANTS_STATUSES.SAFE
                      ? "bg-green-600"
                      : "bg-blue-600",
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_EvacuationStatus_En
                  : row.original.s_EvacuationStatus_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_Photo",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("photo")}
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.s_Photo) return null;

          const attachment = `data:${row.original.s_PhotoContentType};base64,${row.original.s_Photo}`;
          return (
            <div
              onClick={() => onPhotoClick(attachment)}
              className="w-20 h-20 rounded-md overflow-hidden cursor-pointer flex items-center"
            >
              <img src={attachment} alt="participant" />
            </div>
          );
        },
      },
      {
        accessorKey: "s_FullName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("name")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_MobileNo",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("phoneNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Email",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("email")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_EventType_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("eventType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <p>
            {i18n.language === "en"
              ? row.original.s_EventType_En
              : row.original.s_EventType_Ar}
          </p>
        ),
      },
      {
        accessorKey: "s_PersonType_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("userType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <p>
            {i18n.language === "en"
              ? row.original.s_PersonType_En
              : row.original.s_PersonType_Ar}
          </p>
        ),
      },
      {
        accessorKey: "s_ExpectedLocation_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("lastSeen")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <p>
              {t("location")}:{" "}
              {i18n.language === "en"
                ? row.original.s_ExpectedLocation_En
                : row.original.s_ExpectedLocation_Ar}
            </p>
            <p>
              {t("building")}:{" "}
              {i18n.language === "en"
                ? row.original.s_ExpectedBuilding_En
                : row.original.s_ExpectedBuilding_Ar}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "s_MusterPointName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPoint")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>
              {i18n.language === "en"
                ? row.original.s_MusterPointName_En
                : row.original.s_MusterPointName_Ar}
            </p>
          );
        },
      },
      {
        accessorKey: "n_EvacuationMethod",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("method")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.n_EvacuationMethod) return;

          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-1",
                row.original.n_EvacuationMethod === EVACUATION_METHODS.MANUAL
                  ? "bg-blue-600"
                  : row.original.n_EvacuationMethod === EVACUATION_METHODS.QR
                    ? "bg-orange-600"
                    : row.original.n_EvacuationMethod ===
                        EVACUATION_METHODS.FR_READER
                      ? "bg-pink-600"
                      : "bg-blue-600",
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_EvacuationMethod_En
                  : row.original.s_EvacuationMethod_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "action",
        header: () => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
          >
            {t("action")}
          </Button>
        ),
        cell: ({ row }) => {
          if (
            row.original.n_EvacuationStatus !==
            EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING
          )
            return;

          return (
            <div className="flex flex-col items-start gap-1">
              <Button
                onClick={() => handleSafe(row.original.n_EvacuationEventID)}
                Icon={<CheckCheck className="w-5" />}
                className="py-1 bg-green-600 hover:bg-green-700 w-[140px]"
              >
                {t("markSafe")}
              </Button>
              <Button
                onClick={() => handleExited(row.original.n_EvacuationEventID)}
                Icon={<LogOut className="w-5" />}
                className="py-1 w-[140px]"
              >
                {t("markExit")}
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: "s_Remarks",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("remarks")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_ActionByName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "dt_ActionDateTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.dt_ActionDateTime) return;

          return (
            <p>{formattedDateTimeWithoutSec(row.original.dt_ActionDateTime)}</p>
          );
        },
      },
    ],
    [handleExited, handleSafe, i18n.language, onPhotoClick, t],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      {isAllEvacuationEventsParticipantsLoading ? (
        <div className="flex justify-center">
          <Loader />
        </div>
      ) : (
        <TableWrapper
          data={evacuationEventsParticipants}
          columns={columns}
          isSearch
          search={search}
          setSearch={setSearch}
          page={page}
          setPage={setPage}
          totalPages={pages}
          count={count}
          tableId={TABLE_IDS.MUSTER_STATION}
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchStyles="lg:!w-3/4"
          actionsContainerStyles="md:flex-col lg:flex-row"
          ButtonsWrapper={
            <>
              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={evacuationStatuses}
                    placeholder={t("selectStatus")}
                    value={
                      evacuationStatuses?.find(
                        (item) => item.value === Number(value),
                      )?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setSelectedStatus(Number(value));
                    }}
                    styles="py-[5px] mb-[5px] rounded-lg ring-1 ring-purple-primary dark:ring-white focus:outline-none focus:ring-1"
                    valueStyles="dark:text-white"
                    chevronsStyles="dark:text-white"
                  />
                )}
              />

              <QRScannerComponent musterPointId={selectedMusterPoint} />
            </>
          }
          hideColumns={{
            s_Email: false,
            s_EventPersonType_En: false,
            n_EvacuationMethod: false,
            s_EventType_En: false,
            s_Remarks: false,
            s_ActionByName: false,
            dt_ActionDateTime: false,
          }}
        />
      )}

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType="image/png"
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />

      <SafeEvacuationEventParticipantModal
        musterPointId={selectedMusterPoint}
        selectedEventId={selectedEventId}
        isOpen={isSafeEvacuationEventParticipantModalOpen}
        onOpenChange={() => {
          setSelectedEventId(null);
          setIsSafeEvacuationEventParticipantModalOpen(false);
        }}
      />

      <ExitedEvacuationEventParticipantModal
        selectedEventId={selectedEventId}
        isOpen={isExitedEvacuationEventParticipantModalOpen}
        onOpenChange={() => {
          setSelectedEventId(null);
          setIsExitedEvacuationEventParticipantModalOpen(false);
        }}
      />
    </>
  );
};

export default memo(MusterStationTable);
